import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EpisodeCard from "@/components/feature/EpisodeCard";
import EmptyState from "@/components/base/EmptyState";
import { episodes } from "@/mocks/episodes";
import useDocumentMeta from "@/hooks/useDocumentMeta";

type SortKey = "chapter-asc" | "chapter-desc" | "date-desc" | "date-asc";

const PAGE_SIZE = 8;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "chapter-asc", label: "按章节 · 由早到晚" },
  { key: "chapter-desc", label: "按章节 · 由晚到早" },
  { key: "date-desc", label: "按发布时间 · 最新优先" },
  { key: "date-asc", label: "按发布时间 · 最早优先" },
];

export default function Category() {
  useDocumentMeta({
    title: "美国式禁忌2剧集列表 - 剧集概览与排序",
    description:
      "《美国式禁忌2》剧集列表，收录全部已上线剧集，支持按章节编号与发布时间排序并按剧情标签筛选，帮你快速查找与定位想看的剧集内容。",
    keywords: "美国式禁忌2剧集列表,美国式禁忌2全部剧集,美国式禁忌2剧情排序,美国式禁忌2分集介绍",
  });

  const [sortKey, setSortKey] = useState<SortKey>("chapter-asc");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    episodes.forEach((episode) => episode.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    const list = episodes.filter((episode) =>
      activeTags.length === 0 ? true : activeTags.some((tag) => episode.tags.includes(tag))
    );
    const sorted = [...list].sort((a, b) => {
      switch (sortKey) {
        case "chapter-desc":
          return b.number - a.number;
        case "date-desc":
          return a.date < b.date ? 1 : -1;
        case "date-asc":
          return a.date > b.date ? 1 : -1;
        default:
          return a.number - b.number;
      }
    });
    return sorted;
  }, [sortKey, activeTags]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleTag = (tag: string) => {
    setPage(1);
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  const changeSort = (key: SortKey) => {
    setSortKey(key);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-8 md:pt-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "剧集列表" }]} />
        <div className="mt-6 mb-8 md:mb-10">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            美国式禁忌2剧集列表
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            收录《美国式禁忌2》全部已上线剧集，支持按章节编号与发布时间排序，并可按剧情标签快速筛选，帮你快速定位想看的那一集。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8 pb-16">
          <aside className="lg:sticky lg:top-24 self-start rounded-lg bg-background-100 border border-background-200 p-5">
            <div className="mb-6">
              <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-sort-desc text-accent-400" />
                排序方式
              </h2>
              <div className="flex flex-col gap-1.5">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => changeSort(option.key)}
                    className={`text-left px-3 py-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      sortKey === option.key
                        ? "bg-primary-600 text-background-50 font-medium"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-accent-400" />
                  剧情标签
                </h2>
                {activeTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTags([]);
                      setPage(1);
                    }}
                    className="text-xs text-foreground-500 hover:text-accent-400 cursor-pointer transition-colors"
                  >
                    清空
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => {
                  const active = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`h-7 px-3 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors ${
                        active
                          ? "bg-accent-500 text-background-50 font-medium"
                          : "bg-background-200 text-foreground-700 hover:text-accent-300"
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-background-200">
              <p className="text-sm text-foreground-600">
                共 <span className="text-accent-400 font-medium">{filtered.length}</span> 集
                {activeTags.length > 0 && (
                  <span className="ml-2 text-foreground-500">
                    （已筛选：{activeTags.join("、")}）
                  </span>
                )}
              </p>
              <p className="text-sm text-foreground-500">
                第 {safePage} / {totalPages} 页
              </p>
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {pageItems.map((episode) => (
                  <EpisodeCard key={episode.id} episode={episode} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-movie-2-line"
                title="暂无相关剧集"
                description="当前筛选条件下没有匹配的剧集，试试清空筛选条件，或返回首页查看推荐内容。"
                actionLabel="返回首页"
                actionTo="/"
              />
            )}

            {totalPages > 1 && pageItems.length > 0 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                  disabled={safePage === 1}
                  className="h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-800 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent-500 hover:text-accent-300 transition-colors"
                >
                  <i className="ri-arrow-left-s-line" /> 上一页
                </button>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setPage(index + 1)}
                    className={`w-9 h-9 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      safePage === index + 1
                        ? "bg-primary-600 text-background-50 font-medium"
                        : "border border-background-300 text-foreground-800 hover:border-accent-500 hover:text-accent-300"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage === totalPages}
                  className="h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-800 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent-500 hover:text-accent-300 transition-colors"
                >
                  下一页 <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}